import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiService } from '../services/api';
import logger from '../utils/logger';

type Period = 'day' | 'week' | 'month' | 'year';

interface AnalyticsData {
  date: string;
  totalTokens: number;
  totalCost: number;
  requestCount: number;
}

function Analytics() {
  const [data, setData] = useState<AnalyticsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('day');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end: new Date().toISOString().split('T')[0] // today
  });
  const [viewMode, setViewMode] = useState<'cost' | 'tokens' | 'both'>('both');

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getTokenUsageAnalytics(period, dateRange.start, dateRange.end);
      setData(response.data);
      logger.info('Analytics data loaded:', response.data.length, 'data points');
    } catch (err: any) {
      logger.error('Failed to load analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [period, dateRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handlePeriodChange = (newPeriod: Period) => {
    setPeriod(newPeriod);
    
    // Adjust date range based on period
    const end = new Date().toISOString().split('T')[0];
    let start: string;
    
    switch (newPeriod) {
      case 'day':
        start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 30 days
        break;
      case 'week':
        start = new Date(Date.now() - 12 * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 12 weeks
        break;
      case 'month':
        start = new Date(Date.now() - 12 * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 12 months
        break;
      case 'year':
        start = new Date(Date.now() - 5 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 5 years
        break;
    }
    
    setDateRange({ start, end });
  };

  const formatCost = (value: number) => `$${value.toFixed(4)}`;
  const formatTokens = (value: number) => value.toLocaleString();

  const totalStats = data.reduce(
    (acc, curr) => ({
      tokens: acc.tokens + curr.totalTokens,
      cost: acc.cost + curr.totalCost,
      requests: acc.requests + curr.requestCount
    }),
    { tokens: 0, cost: 0, requests: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
        <button
          onClick={loadAnalytics}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">User Analytics</h2>
        <button
          onClick={loadAnalytics}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500">Total Tokens</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{formatTokens(totalStats.tokens)}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500">Total Cost</div>
          <div className="mt-2 text-3xl font-bold text-blue-600">{formatCost(totalStats.cost)}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500">Total Requests</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{totalStats.requests.toLocaleString()}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Period Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
            <div className="flex space-x-2">
              {(['day', 'week', 'month', 'year'] as Period[]).map((p) => (
                <button
                  key={p}
                  onClick={() => handlePeriodChange(p)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    period === p
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="flex items-end space-x-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>

          {/* View Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">View</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as 'cost' | 'tokens' | 'both')}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="both">Both</option>
              <option value="cost">Cost Only</option>
              <option value="tokens">Tokens Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Charts */}
      {data.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-500">No analytics data available for the selected period</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Token Usage Chart */}
          {(viewMode === 'tokens' || viewMode === 'both') && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Token Usage Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={formatTokens} />
                  <Tooltip formatter={(value: number) => formatTokens(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="totalTokens" stroke="#3b82f6" name="Total Tokens" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Cost Chart */}
          {(viewMode === 'cost' || viewMode === 'both') && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={formatCost} />
                  <Tooltip formatter={(value: number) => formatCost(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="totalCost" stroke="#10b981" name="Total Cost ($)" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Request Count Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Count Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="requestCount" stroke="#8b5cf6" name="Request Count" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;

